/** An integer direction or offset in world space. */
export type Vec3 = readonly [number, number, number];

/** The six world directions a room's walls face: +X, −X, +Y, −Y, +Z, −Z. */
export const DIRECTIONS: readonly Vec3[] = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];

const clean = (n: number): number => (n === 0 ? 0 : n); // avoid -0

export function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    clean(a[1] * b[2] - a[2] * b[1]),
    clean(a[2] * b[0] - a[0] * b[2]),
    clean(a[0] * b[1] - a[1] * b[0]),
  ];
}

export function negate(a: Vec3): Vec3 {
  return [clean(-a[0]), clean(-a[1]), clean(-a[2])];
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function subtract(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function equals(a: Vec3, b: Vec3): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/** 0 for X, 1 for Y, 2 for Z (for a unit axis vector). */
export function axisIndex(v: Vec3): 0 | 1 | 2 {
  if (v[0] !== 0) return 0;
  if (v[1] !== 0) return 1;
  return 2;
}

/** HUD label such as "+X" or "−Z". */
export function axisLabel(v: Vec3): string {
  const axis = 'XYZ'[axisIndex(v)];
  const sign = v[axisIndex(v)]! > 0 ? '+' : '−';
  return `${sign}${axis}`;
}
